import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataFeedWizardStore } from '../../store/dataFeedWizard';
import {
  WIZARD_STEPS_CONFIG,
  TOTAL_STEPS,
  WizardStep,
} from './wizardStepsConfig';
import Actions from '../../store/Actions';

export const useDataFeedWizard = () => {
  const navigate = useNavigate();
  const {
    activeStep,
    setActiveStep,
    nextStep,
    prevStep,
    currentStepButtonState,
    stepActions,
    loadSessionData,
    resetWizard,
    getIsRestoredSession,
    markStepCompleted,
    stepLoading,
    completedSteps,
  } = useDataFeedWizardStore();

  const [wizardInitializing, setWizardInitializing] = useState(true);

  // Derive from centralized config
  const currentMetadata = WIZARD_STEPS_CONFIG[activeStep].metadata;

  const handleNext = () => {
    if (activeStep < TOTAL_STEPS - 1) {
      nextStep();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      prevStep();
    }
  };

  const handleBackToDataFeed = useCallback(() => {
    navigate('/data-feed-new');
  }, [navigate]);

  const handleStepClick = useCallback(
    (index: number) => {
      // DEV: Free navigation between steps via stepper
      // Validation is enforced in Next/Back buttons via store actions
      setActiveStep(index);
    },
    [setActiveStep]
  );

  const isFirstStep = activeStep === WizardStep.UPLOAD_FILES;
  const isLastStep = activeStep === TOTAL_STEPS - 1;

  const isStepClickable = (index: number): boolean => {
    return completedSteps.includes(index) || index === activeStep;
  };

  const stepperSteps = WIZARD_STEPS_CONFIG.map((step, index) => ({
    label: step.label,
    completed: completedSteps.includes(index),
    disabled: !isStepClickable(index),
    onClick: () => handleStepClick(index),
  }));

  const handleNextClick = useCallback(async () => {
    // Execute custom onNext if exists (async operations like upload)
    if (stepActions?.onNext) {
      const result = await stepActions.onNext();
      if (result === false) return;

      if (typeof result === 'number') {
        // Negative number = go back to previous step
        if (result < 0) {
          setActiveStep(activeStep + result);
          return;
        }
      }
    }

    // Execute validation if exists (form validation)
    if (stepActions?.validateFields) {
      const isValid = stepActions.validateFields();
      if (!isValid) return;
    }

    markStepCompleted(activeStep);

    // If last step, redirect to data feed page
    if (isLastStep) {
      navigate('/data-feed-new');
      return;
    }

    handleNext();
  }, [
    stepActions,
    handleNext,
    activeStep,
    setActiveStep,
    markStepCompleted,
    isLastStep,
    navigate,
  ]);

  const getButtonProps = useCallback(() => {
    const currentStepConfig = WIZARD_STEPS_CONFIG[activeStep];
    const isRestoredSession = getIsRestoredSession();

    // For Upload step, show "Next" if restored session
    const buttonText =
      activeStep === WizardStep.UPLOAD_FILES && isRestoredSession
        ? 'Next'
        : currentStepConfig.metadata.buttonText || 'Next';

    return {
      onClick: handleNextClick,
      disabled: currentStepButtonState.disabled || stepLoading,
      loading: currentStepButtonState.loading,
      children: isLastStep ? 'Finish' : buttonText,
    };
  }, [
    activeStep,
    handleNextClick,
    isLastStep,
    currentStepButtonState,
    getIsRestoredSession,
    stepLoading,
  ]);

  const initializeWizard = useCallback(
    async (urlSessionId: string | null): Promise<void> => {
      setWizardInitializing(true);
      // Always reset first to ensure fresh state on page visit
      resetWizard();

      try {
        if (urlSessionId) {
          // Resume session only if explicitly provided in URL
          const sessionData = await Actions.getWizardSession(urlSessionId);
          loadSessionData(sessionData);
        }
      } catch (error) {
        console.error('CRITICAL: Wizard initialization failed:', error);
        // TODO: Add toast/snackbar notification for API errors
      } finally {
        setWizardInitializing(false);
      }
    },
    [loadSessionData, resetWizard]
  );

  return {
    activeStep,
    setActiveStep,
    totalSteps: TOTAL_STEPS,
    stepperSteps,
    currentMetadata,
    handleNext,
    handleBack,
    handleBackToDataFeed,
    handleStepClick,
    isFirstStep,
    isLastStep,
    nextButtonProps: getButtonProps(),
    validationMessage: currentStepButtonState.validationMessage,
    initializeWizard,
    wizardInitializing,
  };
};
